import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext"
import { Box, Text } from "@mos-cat/ds"
import {
  $createRangeSelection,
  $nodesOfType,
  $setSelection,
  COMMAND_PRIORITY_EDITOR,
  COMMAND_PRIORITY_LOW,
  createEditor,
  TextNode,
} from "lexical"
import React, { useCallback, useEffect, useRef, useState } from "react"
import { mergeRegister } from "@lexical/utils"

import { $patchStyleText } from "@lexical/selection"

import styles from "./searchPanel.module.css"
import CustomInput from "@src/components/general/CustomInput"
import { Button, Tooltip, useAppContext } from "@dit/core-frontend"
import { ArrowDown, ArrowUp, Close } from "@mos-cat/ds-icons"
import { buttonProps } from "../../components/ToolbarDropDowns"
import { usePageFormContext } from "@src/components/contexts/PageFormContext"
import { REPLACE_ALL, REPLACE_SINGLE, TOGGLE_SEARCH_PANEL } from "./replaceHelpers"
import { onReplaceSingle } from "./onReplaceSingle"
import { onReplaceAll } from "./onReplaceAll"
import { NODES } from "../../context/LexicalComposer"
import { getSearchNodesBySearchValue } from "./searchNodesBySearchValue"

const SearchFullTextPlugin = ({ form }) => {
  const [isOpen, setIsOpen] = useState(false)
  const [editor] = useLexicalComposerContext()
  const lastStateRef = useRef(null)
  const [finishEditorState, setFinishEditorState] = useState(null)
  const { showAlert } = useAppContext()

  const [searchValue, setSearchValue] = useState("")
  const [replaceValue, setReplaceValue] = useState("")
  const [foundNodes, setFoundNodes] = useState([])
  const [isUpdateEnd, setIsUpdateEnd] = useState(false)
  const [аctiveSearchNode, setActiveSearchNode] = useState(0)
  const [isShouldRender, setIsShouldRender] = useState(false)

  const { setIsAutoSaveEnabled } = usePageFormContext()
  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        TOGGLE_SEARCH_PANEL,
        () => {
          form.change("isAutoSaveEnabled", false)

          setIsAutoSaveEnabled?.(false)
          setIsOpen((prev) => !prev)
          editor.setEditable(false)
        },
        COMMAND_PRIORITY_LOW,
      ),
      editor.registerCommand(REPLACE_SINGLE, onReplaceSingle, COMMAND_PRIORITY_EDITOR),
      editor.registerCommand(REPLACE_ALL, onReplaceAll, COMMAND_PRIORITY_EDITOR),
    )
  }, [isOpen, editor])

  const handleReplace = () => {
    const EditorStateJSON = lastStateRef.current.toJSON()
    const newEditor = createEditor({
      nodes: NODES,
      disableEvents: false,
      editable: true,
      onError: (err) => console.error(err),
    })

    newEditor.update(() => {
      const editorState = newEditor.parseEditorState(EditorStateJSON)
      newEditor.setEditorState(editorState)
    })

    editor.update(
      () => {
        editor.setEditable(true)
        editor.setEditorState(newEditor.getEditorState())
        lastStateRef.current = null
      },
      { discrete: true, tag: "historic" },
    )
    editor.dispatchCommand(REPLACE_SINGLE, {
      from: searchValue,
      to: replaceValue,
      replaceNode: foundNodes[аctiveSearchNode],
      nodeIndex: аctiveSearchNode,
      lastStateRef: lastStateRef,
    })

    editor.update(
      () => {
        const textNodes = $nodesOfType(TextNode)
        for (const node of textNodes) {
          const style = node.getStyle()
          const styleToRemove = "background:#FFAD32; border-radius:1px;"
          const newStyle = style.replace(styleToRemove, "")

          if (style === "background:#FFAD32; border-radius:1px;") {
            node.setStyle(newStyle)
          }
        }
      },
      { discrete: true, tag: "historic" },
    )
    setTimeout(() => {
      editor.setEditable(false)
      void setIsShouldRender(true)
      setIsUpdateEnd(true)
    }, 0)
  }

  const handleReplaceAll = () => {
    const EditorStateJSON = lastStateRef.current.toJSON()
    const newEditor = createEditor({
      nodes: NODES,
      disableEvents: false,
      editable: true,
      onError: (err) => console.error(err),
    })

    newEditor.update(() => {
      const editorState = newEditor.parseEditorState(EditorStateJSON)
      newEditor.setEditorState(editorState)
    })

    editor.update(
      () => {
        editor.setEditable(true)
        editor.setEditorState(newEditor.getEditorState())
        lastStateRef.current = null
      },
      { discrete: true, tag: "historic" },
    )

    editor.dispatchCommand(REPLACE_ALL, {
      from: searchValue,
      to: replaceValue,
      lastStateRef: lastStateRef,
    })

    editor.update(
      () => {
        editor.setEditable(false)
      },
      { discrete: true, tag: "historic" },
    )

    showAlert(`Готово. Выполнено замен: ${foundNodes.length}`, { type: "success" })
    setIsShouldRender(true)
    setIsUpdateEnd(true)
  }

  const onChange = (value) => {
    setSearchValue(value)
  }
  const onReplace = (value) => {
    setReplaceValue(value)
  }
  useEffect(() => {
    setActiveSearchNode(0)
    if (!searchValue) {
      setFoundNodes([])
    }
  }, [searchValue])

  useEffect(() => {
    if (!isOpen && isUpdateEnd) {
      setIsUpdateEnd(false)
      editor.setEditable(true)
    }
    if (isOpen && !isUpdateEnd) {
      lastStateRef.current = editor.getEditorState()
      editor.setEditable(false)
    }
  }, [editor, isOpen, finishEditorState, isUpdateEnd])

  useEffect(() => {
    editor.update(
      () => {
        const textNodes = $nodesOfType(TextNode)

        for (const node of textNodes) {
          const style = node?.getStyle()
          if (style) {
            const OrangeBackground = "background: #FFAD32;border-radius: 1px;"
            const yellowBackground = "background: rgba(255, 238, 50, 1);border-radius: 1px;"
            const newStyle = style?.replace(OrangeBackground, "").replace(yellowBackground, "")
            node?.setStyle(newStyle || "")
          }
        }
      },
      { discrete: true, tag: "historic" },
    )

    if (searchValue || isShouldRender) {
      const nodes = []
      const finishNodes = []
      editor.update(
        () => {
          const textNodes = $nodesOfType(TextNode)

          const searchData = getSearchNodesBySearchValue(textNodes, searchValue)
          nodes.push(...searchData)
        },
        { discrete: true, tag: "historic" },
      )

      editor.update(
        () => {
          for (let i = nodes.length - 1; i >= 0; i--) {
            const isActiveNode = nodes[i].id === nodes[аctiveSearchNode]?.id
            const item = nodes[i]

            if (item.nodesData?.length === 1) {
              const rangeSelection = $createRangeSelection()
              const text = item.nodesData[0].getTextContent()

              rangeSelection.setTextNodeRange(
                item.nodesData[0],
                item.startIndex,
                item.nodesData[0],
                text.length > item.endIndex + 1 ? item.endIndex + 1 : text.length,
              )

              try {
                if (rangeSelection !== null) {
                  $patchStyleText(rangeSelection, {
                    background: isActiveNode ? "#FFAD32" : "rgba(255, 238, 50, 1)",
                    "border-radius": "1px",
                  })
                  $setSelection(rangeSelection)
                }
              } catch (err) {
                console.error("Ошибка", err)
              }
            }
            if (item.nodesData?.length >= 2) {
              const items = item.nodesData
              const rangeSelection = $createRangeSelection()
              const textFirst = items[0].node.getTextContent()
              const textEnd = items[items.length - 1].node.getTextContent()

              rangeSelection.setTextNodeRange(
                items[0].node,
                items[0].matchText.length === textFirst.length
                  ? 0
                  : textFirst.length - items[0].matchText.length,
                items[items.length - 1].node,
                items[items.length - 1].matchText.length === textEnd.length
                  ? textEnd
                  : items[items.length - 1].matchText.length,
              )

              $patchStyleText(rangeSelection, {
                background: isActiveNode ? "#FFAD32" : "rgba(255, 238, 50, 1)",
                "border-radius": "1px",
              })
              $setSelection(rangeSelection)
            }
          }
        },
        { discrete: true, tag: "historic" },
      )
      editor.update(
        () => {
          const textNodes = $nodesOfType(TextNode)
          const searchData = getSearchNodesBySearchValue(textNodes, searchValue)
          finishNodes.push(...searchData)
        },
        { discrete: true, tag: "historic" },
      )
      setFoundNodes(nodes)
      setIsShouldRender(false)
    }
  }, [searchValue, isShouldRender, аctiveSearchNode, editor])
  const scrollToElem = (index) => {
    if (!foundNodes) {
      return null
    }

    const elem = editor.getElementByKey(foundNodes[index]?.nodesData[0]?.__key)
    if (elem) {
      elem?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      })
    }
  }

  const nextItem = () => {
    if (!foundNodes.length || аctiveSearchNode >= foundNodes.length - 1) {
      setActiveSearchNode(0)
      scrollToElem(0)
    } else {
      setActiveSearchNode((prev) => ++prev)
      scrollToElem(аctiveSearchNode + 1)
    }
  }

  const prevItem = () => {
    if (аctiveSearchNode < 1) {
      setActiveSearchNode(foundNodes.length - 1)
      scrollToElem(foundNodes.length - 1)
    } else {
      setActiveSearchNode((prev) => prev - 1)
      scrollToElem(аctiveSearchNode - 1)
    }
  }

  useEffect(() => {
    const handleKeyDown = (event) => {
      // Проверяем, нажата ли комбинация Ctrl+F
      if (
        !isOpen &&
        ((event.metaKey && event.key === "f") || (event.ctrlKey && event.key === "f"))
      ) {
        event.preventDefault() // Отключаем стандартное окно поиска браузера
        setIsOpen(true)
      }
    }

    window.addEventListener("keydown", handleKeyDown)

    return () => {
      window.removeEventListener("keydown", handleKeyDown)
    }
  }, [isOpen])

  const handleOutsideClick = useCallback(() => {
    setTimeout(() => {
      if (isOpen) {
        if (!isUpdateEnd) {
          const EditorStateJSON = lastStateRef.current.toJSON()
          const newEditor = createEditor({
            nodes: NODES,
            disableEvents: false,
            editable: true,
            onError: (err) => console.error(err),
          })

          newEditor.update(() => {
            const editorState = newEditor.parseEditorState(EditorStateJSON)
            newEditor.setEditorState(editorState)
          })

          editor.update(
            () => {
              editor.setEditable(true)
              editor.setEditorState(newEditor.getEditorState())
              lastStateRef.current = null
            },
            { discrete: true, tag: "historic" },
          )
        }
        lastStateRef.current = null
        setReplaceValue("")
        setSearchValue("")
        setFoundNodes([])
        form.change("isAutoSaveEnabled", true)
        setIsOpen(false)
        setIsAutoSaveEnabled?.(true)
      }
    }, 0)
  }, [isOpen, editor, isUpdateEnd])

  return (
    <>
      {isOpen && (
        <Box className={styles.searchPanelItems}>
          <Text fontWeight="600"> Найти:</Text>
          <CustomInput value={searchValue} onChange={onChange} className={styles.searchInput} />
          <Text>Результатов: {foundNodes.length}</Text>
          <Box marginLeft="16px" display="flex" alignItems="center" gridGap="10px">
            <Button
              {...buttonProps}
              styles={{
                "& > *": {
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                },
                path: {
                  fill: "#959EAB",
                },
                "&:hover path": {
                  fill: foundNodes.length && "#13151A",
                },
                "&:hover > *, &:hover > * > *": {
                  backgroundColor: foundNodes.length && "#FFFFFF",
                },
              }}
              kind="borderless"
              disabled={!foundNodes.length}
              onClick={nextItem}
            >
              {" "}
              <ArrowDown />
            </Button>
            <Button
              {...buttonProps}
              kind="borderless"
              {...buttonProps}
              styles={{
                "& > *": {
                  width: "24px",
                  height: "24px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: "4px",
                },
                path: {
                  fill: "#959EAB",
                },
                "&:hover path": {
                  fill: foundNodes.length && "#13151A",
                },
                "&:hover > *, &:hover > * > *": {
                  borderRadius: "4px",

                  backgroundColor: foundNodes.length && "#FFFFFF",
                },
              }}
              disabled={!foundNodes.length}
              onClick={prevItem}
            >
              <ArrowUp />
            </Button>
          </Box>
          <Box className={styles.separator}></Box>

          <Text fontWeight="600">Заменить на: </Text>
          <CustomInput value={replaceValue} onChange={onReplace} className={styles.searchInput} />
          <Box display="flex" alignItems="center" gridGap="17px">
            <Button
              {...buttonProps}
              kind="borderless"
              onClick={handleReplace}
              color="#003099"
              styles={{
                "& > *": {
                  fontWeight: "400",
                  color: !searchValue || !foundNodes?.length ? "#959EAB" : "#003099",
                },
              }}
              disabled={!searchValue || !foundNodes?.length}
            >
              Заменить
            </Button>
            <Button
              {...buttonProps}
              kind="borderless"
              onClick={handleReplaceAll}
              styles={{
                "& > *": {
                  fontWeight: "400",
                  color: !searchValue || !foundNodes?.length ? "#959EAB" : "#003099",
                },
              }}
              disabled={!searchValue || !foundNodes?.length}
            >
              Заменить все
            </Button>
          </Box>
          <Box className={styles.separator} styles={{ marginRight: "0px" }}></Box>
          <Box>
            <Tooltip content="Закрыть">
              <Button
                onClick={handleOutsideClick}
                kind="borderless"
                {...buttonProps}
                styles={{
                  path: {
                    fill: "#5D6570",
                  },
                  "& > *": {
                    fontWeight: "400",
                    marginLeft: "8px",
                    width: "24px",
                    height: "24px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: "4px",
                  },
                }}
              >
                <Close />
              </Button>
            </Tooltip>
          </Box>
        </Box>
      )}
    </>
  )
}

export default SearchFullTextPlugin
